import React from 'react';
import { getThumbnailUrl, getFlagUrl } from '../utils/imageUtils';
import LazyImage from './LazyImage';
import LazyFlag from './LazyFlag';

function ImageGrid({ images, lastImageRef, containerWidth, containerHeight, onPortraitClick, isMobile, highlightLastTwoRows = false, onGridScroll, showJoinBottomHint = false, joinPrimaryText, joinSecondaryText }) {

  return (
    <div className="image-grid-container" style={{ width: '100%', padding: '2rem 0' }} onScroll={onGridScroll}>
      <div className={`image-grid ${highlightLastTwoRows ? 'gate-highlight' : ''}`}>
        {images.map((img, index) => {
          const isLast = index === images.length - 1;
          const imageUrl = getThumbnailUrl(img.thumb_path, img.image_path);
          
          return (
            <div
              key={img.uuid}
              ref={isLast ? lastImageRef : null}
              className={`image-container ${img.isNew ? 'new' : ''}`}
              style={{
                width: `${containerWidth}px`,
                height: `${containerHeight}px`,
                margin: isMobile ? '0' : '20px',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease, opacity 0.25s ease',
                willChange: 'transform',
                contain: 'layout paint style',
                backfaceVisibility: 'hidden',
              }}
              role="button"
              tabIndex={0}
              aria-label={(img.nickname ? `${img.nickname} – ` : '') + 'View memory'}
              onClick={() => onPortraitClick(img)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPortraitClick(img);
                }
              }}
              onMouseEnter={(e) => {
                // Add subtle cosmic glow on hover
                e.currentTarget.style.boxShadow = `
                  0 25px 60px rgba(0, 0, 0, 0.5),
                  0 15px 40px rgba(0, 212, 255, 0.3),
                  0 8px 20px rgba(255, 107, 157, 0.2),
                  0 0 30px rgba(0, 212, 255, 0.1)
                `;
              }}
              onMouseLeave={(e) => {
                // Reset glow on leave
                e.currentTarget.style.boxShadow = `
                  0 4px 20px rgba(0, 0, 0, 0.3),
                  0 2px 10px rgba(0, 212, 255, 0.1)
                `;
              }}
            >
              {/* Lazy loaded main image */}
              <LazyImage
                src={imageUrl}
                alt={img.nickname || 'Memory portrait'}
                placeholder="rgba(0, 0, 0, 0.1)"
                lazyOptions={{
                  rootMargin: isMobile ? '100px' : '200px',
                  threshold: 0.01
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 1
                }}
              />
              
              {/* Cosmic overlay effect */}
              <div className="image-cosmic-overlay" style={{ zIndex: 2 }} />
              
              {/* Enhanced image overlay */}
              <div className="image-overlay" style={{ zIndex: 3 }}>
                <div className="overlay-content">
                  <span className="nickname">{img.nickname}</span>
                  {img.country && (
                    <div className="country-flag">
                      <LazyFlag
                        src={getFlagUrl(img.country)}
                        alt={img.country}
                        style={{ 
                          width: '18px', 
                          height: '14px',
                          marginLeft: '8px',
                          filter: 'drop-shadow(0 0 5px rgba(255, 255, 255, 0.3))'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Hover cue (desktop/hover-capable only) */}
              <div className="hover-cue" aria-hidden="true" style={{ zIndex: 5 }}>
                <span className="hover-cue-text">View</span>
              </div>
              
              {/* New memory indicator */}
              {img.isNew && (
                <div className="new-memory-indicator" style={{ zIndex: 4 }}>
                  <span className="indicator-text">NEW</span>
                </div>
              )}
            </div>
          );
        })}

        {/* Bottom-of-scroll join message placed INSIDE the grid so it
            appears directly after the last profile card */}
        {showJoinBottomHint && (
          <div className="join-bottom-hint" style={{ width: '100%' }}>
            <div className="primary">{joinPrimaryText}</div>
            <div className="secondary">{joinSecondaryText}</div>
          </div>
        )}
      </div>
      
      {/* Cosmic grid background effect */}
      <div className="grid-background-effect" />
      {/* Spacer to ensure hint isn't overlapped by fixed bottom elements */}
      {showJoinBottomHint && <div style={{ height: 24 }} />}
    </div>
  );
}

export default React.memo(ImageGrid);
